import type { LessonData } from '@/hooks/useLanguageLesson';
import type { VocabularyEntry } from '@/hooks/useLanguageLearning';

export type ExerciseKind = 'meaning' | 'listen' | 'recall' | 'build';
export interface LanguagePracticeItem { word: string; translation: string; }
export interface LanguageExercise {
  /** Content identity, unchanged when order, choices or exercise modality changes. */
  id: string;
  kind: ExerciseKind;
  prompt: string;
  answer: string;
  word: string;
  translation: string;
  choices: string[];
  tokens: string[];
}
export interface PracticeResult { score: number; correct: string[]; mistakes: string[]; }
export interface PracticeItemEvidence {
  itemId: string;
  attempts: number;
  correct: number;
  lastKind?: ExerciseKind;
}
export interface PracticeSelectionOptions {
  /** Defaults to seed, preserving the existing createExercises(round) callers. */
  attempt?: number;
  /** Canonical item IDs or words returned in PracticeResult.mistakes. */
  mistakes?: readonly string[];
  /** When supplied, absent items are treated as not yet assessed. */
  evidence?: readonly PracticeItemEvidence[];
  /** Keep rounds short. Values outside 1..10 are clamped. */
  limit?: number;
}

export const LESSON_PASS_SCORE = 80;

// Punctuation/case/width are not the learning target. Accents remain significant.
// Use locale-independent casing so identities do not change with device locale.
export const normalizeAnswer = (text: string) => typeof text === 'string'
  ? text.normalize('NFKC').toLowerCase().replace(/[\p{P}\p{S}]/gu, ' ').replace(/\s+/g, ' ').trim()
  : '';
export const answerMatches = (actual: string, expected: string) => !!normalizeAnswer(actual) && normalizeAnswer(actual) === normalizeAnswer(expected);

export function languagePracticeItemId(word: string, translation: string): string {
  // Store the full normalized pair rather than a short hash with collision risk.
  return `language-item:${JSON.stringify([normalizeAnswer(word), normalizeAnswer(translation)])}`;
}

function integer(value: number | undefined, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(Number.MAX_SAFE_INTEGER, Math.max(0, Math.trunc(value)))
    : fallback;
}

function hash(text: string): number {
  let value = 2166136261;
  for (let i = 0; i < text.length; i++) value = Math.imul(value ^ text.charCodeAt(i), 16777619);
  return value >>> 0;
}

export function shuffled<T>(items: readonly T[], seed: number): T[] {
  const result = [...items];
  let state = integer(seed) >>> 0;
  const next = () => {
    // Mix the output bits: taking an LCG's low bits directly restricts which
    // four-choice permutations are reachable, even with Fisher-Yates.
    state = (state + 0x6D2B79F5) >>> 0;
    let value = Math.imul(state ^ (state >>> 15), state | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return (value ^ (value >>> 14)) >>> 0;
  };
  const sample = (max: number) => {
    // Rejection sampling avoids the modulo bias of state % max.
    const bound = Math.floor(0x100000000 / max) * max;
    let value: number;
    do { value = next(); } while (value >= bound);
    return value % max;
  };
  for (let i = result.length - 1; i > 0; i--) {
    const j = sample(i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function uniqueItems(items: readonly LanguagePracticeItem[]): LanguagePracticeItem[] {
  const unique = new Map<string, LanguagePracticeItem>();
  if (!Array.isArray(items)) return [];
  for (const item of items) {
    if (!item || typeof item.word !== 'string' || typeof item.translation !== 'string') continue;
    if (!normalizeAnswer(item.word) || !normalizeAnswer(item.translation)) continue;
    const id = languagePracticeItemId(item.word, item.translation);
    if (!unique.has(id)) unique.set(id, { word: item.word.trim(), translation: item.translation.trim() });
  }
  return [...unique.values()];
}

function evidenceByItem(evidence?: readonly PracticeItemEvidence[]): Map<string, PracticeItemEvidence> {
  const result = new Map<string, PracticeItemEvidence>();
  if (!Array.isArray(evidence)) return result;
  for (const item of evidence) {
    if (!item || typeof item.itemId !== 'string' || !Number.isSafeInteger(item.attempts) || item.attempts < 0
      || !Number.isSafeInteger(item.correct) || item.correct < 0 || item.correct > item.attempts) continue;
    result.set(item.itemId, item);
  }
  return result;
}

/** Build a bounded round from the complete bank, retaining coverage on retries. */
export function buildExercises(items: readonly LanguagePracticeItem[], seed = 0, options: PracticeSelectionOptions = {}): LanguageExercise[] {
  const unique = uniqueItems(items);
  if (!unique.length) return [];
  const round = integer(options.attempt, integer(seed));
  const limit = Math.min(unique.length, Math.max(1, Math.min(10, integer(options.limit, 10))));
  const coverageSlots = Math.ceil(limit / 2);
  const evidence = evidenceByItem(options.evidence);
  const mistakeValues = Array.isArray(options.mistakes) ? options.mistakes.filter(x => typeof x === 'string') : [];
  const mistakeIds = new Set(mistakeValues);
  const mistakeWords = new Set(mistakeValues.map(normalizeAnswer));
  const isMistake = (item: LanguagePracticeItem) => {
    const id = languagePracticeItemId(item.word, item.translation), assessed = evidence.get(id);
    return mistakeIds.has(id) || mistakeWords.has(normalizeAnswer(item.word))
      || !!assessed && assessed.attempts > 0 && assessed.correct === 0;
  };

  // A fixed bank permutation plus rotating windows guarantees coverage. Fresh
  // shuffles alone can repeatedly omit the same words. Start positions advance
  // by at most half a round, reserving coverage even with persistent mistakes.
  const sorted = [...unique].sort((a, b) => {
    const left = languagePracticeItemId(a.word, a.translation), right = languagePracticeItemId(b.word, b.translation);
    return left < right ? -1 : left > right ? 1 : 0;
  });
  const bank = shuffled(sorted, hash(sorted.map(item => languagePracticeItemId(item.word, item.translation)).join('\n')));
  const priorities = bank.filter(isMistake);
  // A one-item round cannot contain both goals: alternate repair and coverage.
  const singleSlotRepair = limit === 1 && priorities.length > 0;
  const coverageRound = singleSlotRepair ? Math.floor(round / 2) : round;
  const priorityCount = Math.min(singleSlotRepair ? round % 2 : Math.floor(limit / 2), priorities.length);
  const selected: LanguagePracticeItem[] = [];
  const selectedIds = new Set<string>();
  const add = (item: LanguagePracticeItem) => {
    const id = languagePracticeItemId(item.word, item.translation);
    if (!selectedIds.has(id)) { selected.push(item); selectedIds.add(id); }
  };
  for (let i = 0; i < priorityCount; i++) add(priorities[((coverageRound % priorities.length) * priorityCount + i) % priorities.length]);
  const start = ((coverageRound % bank.length) * coverageSlots) % bank.length;
  for (let i = 0; selected.length < limit && i < bank.length; i++) add(bank[(start + i) % bank.length]);

  return shuffled(selected, hash(`round:${round}:seed:${integer(seed)}`)).map(item => {
    const id = languagePracticeItemId(item.word, item.translation);
    const itemEvidence = evidence.get(id);
    const tokens = item.word.split(/\s+/);
    const canBuild = tokens.length >= 2 && tokens.length <= 14;
    const mistaken = isMistake(item);
    // Without explicit evidence, only infer prior exposure for the deterministic
    // ordinary rounds. A retry with custom priorities cannot infer unseen items.
    const previouslyCovered = round > 0 && bank.indexOf(item) < Math.min(bank.length, (round - 1) * coverageSlots + limit);
    const seen = itemEvidence ? itemEvidence.attempts > 0
      : options.evidence !== undefined || mistakeValues.length > 0 ? mistaken : previouslyCovered;
    const needsSupport = mistaken || (itemEvidence !== undefined && itemEvidence.correct === 0);
    const kinds: ExerciseKind[] = !seen || needsSupport
      ? ['meaning', 'listen']
      : canBuild ? ['meaning', 'listen', 'build', 'recall'] : ['meaning', 'listen', 'recall'];
    let kind = kinds[(hash(id) + round) % kinds.length];
    if (itemEvidence?.lastKind === kind && kinds.length > 1) kind = kinds[(kinds.indexOf(kind) + 1) % kinds.length];

    // Alternate valid translations for this same word are not wrong answers.
    const meaningsOfWord = new Set(unique.filter(other => normalizeAnswer(other.word) === normalizeAnswer(item.word)).map(other => normalizeAnswer(other.translation)));
    const distractorMap = new Map<string, string>();
    for (const other of unique) {
      const translation = normalizeAnswer(other.translation);
      if (!meaningsOfWord.has(translation) && !distractorMap.has(translation)) distractorMap.set(translation, other.translation);
    }
    const distractors = [...distractorMap.values()];
    if ((kind === 'meaning' || kind === 'listen') && !distractors.length) kind = canBuild ? 'build' : 'recall';
    const recognition = kind === 'meaning' || kind === 'listen';
    const variantSeed = hash(`${id}:${round}:${integer(seed)}`);
    return {
      id, kind, word: item.word, translation: item.translation,
      prompt: recognition ? item.word : item.translation,
      answer: recognition ? item.translation : item.word,
      choices: recognition ? shuffled([item.translation, ...shuffled(distractors, variantSeed).slice(0, 3)], variantSeed + 1) : [],
      tokens: kind === 'build' ? shuffled(tokens, variantSeed + 2) : [],
    };
  });
}

export function lessonExercises(lesson: LessonData, seed = 0, options: PracticeSelectionOptions = {}) {
  const vocabulary = Array.isArray(lesson?.vocabulary) ? lesson.vocabulary : [];
  const phrases = Array.isArray(lesson?.phrases) ? lesson.phrases : [];
  const expressions = Array.isArray(lesson?.expressions) ? lesson.expressions : [];
  return buildExercises([
    ...vocabulary.filter(Boolean).map(v => ({ word: v.word, translation: v.translation })),
    ...phrases.filter(Boolean).map(p => ({ word: p.phrase, translation: p.translation })),
    ...expressions.filter(Boolean).map(e => ({ word: e.expression, translation: e.meaning })),
  ], seed, options);
}

// Transparent initial intervals; this is a review priority, not a fluency estimate.
export function reviewDue(word: VocabularyEntry, now = Date.now()) {
  if (!word.last_practiced || !Number.isFinite(Date.parse(word.last_practiced))) return true;
  const days = word.mastery < 30 ? 1 : word.mastery < 60 ? 3 : word.mastery < 80 ? 7 : 14;
  return now - Date.parse(word.last_practiced) >= days * 86400000;
}
export function selectReviewWords(words: VocabularyEntry[], now = Date.now()) {
  return words.filter(w => reviewDue(w, now)).sort((a,b) => a.mastery - b.mastery || (Date.parse(a.last_practiced || '') || 0) - (Date.parse(b.last_practiced || '') || 0)).slice(0, 10);
}
export function languageLessonId(language: string, level: string, index: number) { return level === 'beginner' ? `${language}-0${index}` : `${language}-${level}-0${index}`; }
